import ControlledForm from "../components/form/controlled-form.tsx";
import ControlledInput from "../components/form/controlled-input.tsx";
import { ControlledDropdown } from "../components/form/controlled-dropdown.tsx";
import { MaterialSelectProps } from "../utils/types/material-select.props.ts";
import { PlateSelectProps } from "../utils/types/plate-select.props.ts";
import { useFormContext } from "react-hook-form";
import { useEffect } from "react";
import { Button } from "../components/ui/button.tsx";
import { IoCheckmark } from "react-icons/io5";
import { FaTrashAlt } from "react-icons/fa";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toaster } from "../components/ui/toaster.tsx";
import { useNavigate } from "react-router-dom";
import { api } from "../api/custom-api.ts";
import { CreateCodebookWarehouseRequest } from "@ns/api";
import {
  CreateCodebook,
  createCodebookSchema,
} from "../schemas/create-codebook.schema.ts";
import ControlledTextArea from "../components/form/controlled-textarea.tsx";

const CreateCodebookForm = () => {
  const {
    handleSubmit,
    reset,
    formState: { isValid },
  } = useFormContext<CreateCodebook>();

  const navigate = useNavigate();

  const resetFields = () => {
    reset({
      code: "",
      // @ts-ignore
      type: [""],
      // @ts-ignore
      plateType: [""],
      name: "",
      note: "",
      stoneSurfaceTreatment: "",
    });
  };

  useEffect(() => {
    resetFields();
  }, []);

  const queryClient = useQueryClient();

  const { mutate } = useMutation({
    mutationFn: (request: CreateCodebookWarehouseRequest) =>
      api.codebookWarehouse.createCodebookWarehouse(request),
    onSuccess: () => {
      toaster.create({
        title: "Kreiran novi nalog!",
        // @ts-expect-error
        placement: "bottom-end",
        description: "Novi nalog je uspešno kreiran",
        type: "info",
        duration: 5000,
      });
      resetFields();
      queryClient.invalidateQueries({ queryKey: ["codebookWarehouse"] });
      navigate("/codebooks");
    },
    onError: () => {
      toaster.create({
        title: "Greška!",
        // @ts-expect-error
        placement: "bottom-end",
        description: "Proverite unete podatke",
        type: "error",
        duration: 5000,
      });
    },
  });

  const onSubmit = handleSubmit((data) => {
    mutate({
      code: data.code,
      name: data.name,
      type: data.type[0],
      plateType: data.plateType[0],
      note: data.note,
      stoneSurfaceTreatment: data.stoneSurfaceTreatment,
    });
  });

  return (
    <div className="pt-[5.25rem] h-full xl:h-full p-[1rem] bg-[#ffffff] flex flex-col items-center">
      <div className="w-full flex flex-col sm:flex-row justify-between items-center px-[0.25rem]">
        <div className="flex flex-row gap-[1rem] items-center">
          <span className="text-[24px] text-center text-black font-semibold">
            DODAJ NOVI PROIZVOD U ŠIFARNIK
          </span>
        </div>
        <div className="flex flex-row gap-[1rem] items-center">
          <div className="w-[10rem] sm:w-[12rem]">
            <Button
              size={"sm"}
              colorPalette={"black"}
              variant={"surface"}
              width={"full"}
              color={"#000000"}
              backgroundColor={"#ffffff"}
              borderColor={"#ffffff"}
              fontWeight={"600"}
              fontSize={"14px"}
              borderRadius={"0.5rem"}
              disabled={!isValid}
              onClick={onSubmit}
            >
              <IoCheckmark /> Potvrdi
            </Button>
          </div>
          <div className="w-[10rem] sm:w-[12rem]">
            <Button
              size={"sm"}
              colorPalette={"black"}
              variant={"surface"}
              width={"full"}
              color={"#000000"}
              backgroundColor={"#ffffff"}
              borderColor={"#ffffff"}
              fontWeight={"600"}
              fontSize={"14px"}
              borderRadius={"0.5rem"}
              onClick={() => resetFields()}
            >
              <FaTrashAlt /> Poništi
            </Button>
          </div>
        </div>
      </div>
      <div className="w-full h-full flex flex-col gap-[1rem] mt-[1rem] items-center overflow-scroll">
        <div className="w-full flex flex-col items-center gap-[1rem] justify-between mt-[1rem] px-[0.25rem]">
          <ControlledInput
            className="w-full sm:w-[24rem] md:w-[28rem]"
            name={"code"}
            placeholder={"Unesi šifru materijala"}
            label={"Šifra materijala"}
          />
          <ControlledInput
            className="w-full sm:w-[24rem] md:w-[28rem]"
            name={"name"}
            placeholder={"Unesi naziv materijala"}
            label={"Naziv materijala"}
          />
          <ControlledDropdown
            name={"type"}
            className="w-full sm:w-[24rem] md:w-[28rem]"
            label={"Tip materijala"}
            placeholder={"Izaberi tip materijala"}
            options={MaterialSelectProps}
          />
          <ControlledDropdown
            name={"plateType"}
            className="w-full sm:w-[24rem] md:w-[28rem]"
            label={"Tip ploče"}
            placeholder={"Izaberi tip ploče"}
            options={PlateSelectProps}
          />
          <ControlledInput
            className="w-full sm:w-[24rem] md:w-[28rem]"
            name={"stoneSurfaceTreatment"}
            placeholder={"Unesi obradu površine kamena (opciono)"}
            label={"Obrada površine kamena"}
          />
          <ControlledTextArea
            className="w-full sm:w-[24rem] md:w-[28rem]"
            name={"note"}
            placeholder={"Unesi napomenu (opciono)"}
            label={"Napomena"}
          />
        </div>
      </div>
    </div>
  );
};

export const CreateCodebookPage = () => {
  return (
    <ControlledForm schema={createCodebookSchema}>
      <CreateCodebookForm />
    </ControlledForm>
  );
};
